import clsx from 'clsx';
import type { RxDocument } from 'rxdb';
import { type ReactNode } from 'react';

import { TagsSet } from '@/components/tags-set';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Popover } from '@flintmint/web-shared/components/popover';
import { LastSyncEditTime } from '@/components/last-sync-edit-time';
import { InlineInput } from '@flintmint/web-shared/components/inline-input';
import { MAX_DOCUMENT_NAME_LENGTH } from '@/extensions/content-size-limit';

import type { Note, UserSource } from '@flintmint/core-shared/rxdb';
import type { MenuProps } from '@flintmint/web-shared/components/menu';

import styles from './document-header.module.scss';

type Document = Note | UserSource;

export const DocumentHeader = <T extends Document>(props: DocumentHeaderProps<T>) => {
  const {
    name,
    menu,
    error,
    bottom,
    onChange,
    tagIds,
    document,
    disabled,
    leftActions,
    lastSyncTime,
    titleContent,
    lastEditTime,
    rightActions,
    documentExists,
    upsertDocument,
    processing = false,
    ...rest
  } = props;

  const renderStatus = () => {
    return (
      <div
        className={clsx(styles.processing, {
          [styles.processingFailed]: error,
        })}
      >
        {processing && !error && <Loader size={13} variant="steps" color="warning" data-testid="processing-spinner" />}
        {error && <Icon type="cancel-circle-half-dot" data-testid="processing-failed-icon" />}
        <span>{error || 'Processing'}</span>
      </div>
    );
  };

  return (
    <div className={styles.documentHeader} {...rest}>
      <div className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.titleBlock}>
            <div className={styles.title}>
              <InlineInput
                value={name}
                disabled={disabled}
                maxLength={MAX_DOCUMENT_NAME_LENGTH}
                data-testid="inline-input"
                className={styles.inlineInput}
                onChange={(e) => onChange(e.target.value)}
              />

              {documentExists && (
                <Popover menu={menu} placement="bottom" offset={[0, 0]} maxWidth={164}>
                  <Icon type="more-horizontal" data-testid="document-header-menu-icon" />
                </Popover>
              )}
            </div>

            {titleContent}

            {processing || error ? (
              renderStatus()
            ) : (
              <LastSyncEditTime lastSyncTime={lastSyncTime} lastEditTime={lastEditTime} className={styles.lastSync} />
            )}
          </div>

          {rightActions && <div className={styles.rightActions}>{rightActions}</div>}
        </div>

        <div className={styles.bottomRow}>
          <div className={styles.leftActions}>{leftActions}</div>

          <div className={styles.tags}>
            <TagsSet
              tagIds={document?.tagIds ?? tagIds}
              disabled={disabled}
              onChange={(selections) => {
                return upsertDocument({
                  tagIds: selections,
                } as Partial<T>);
              }}
            />
          </div>
        </div>
      </div>

      {bottom}
    </div>
  );
};

export interface DocumentHeaderProps<T = Document> {
  name: string;
  error?: string;
  bottom?: ReactNode;
  lastSyncTime?: string;
  lastEditTime?: string;
  documentExists?: boolean;
  processing?: boolean;
  menu?: MenuProps['data'];
  rightActions?: ReactNode;
  leftActions?: ReactNode;
  titleContent?: ReactNode;
  tagIds?: string[];
  document?: RxDocument<T>;
  disabled?: boolean;
  onChange: (value: string) => void;
  upsertDocument: (payload: Partial<T>) => Promise<any>;
}
